import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const BeltTimeDialog = ({ open, onClose, beltNumber, currentSchedule, onSave, beltName }) => {
  const [openTime, setOpenTime] = useState('03:00');
  const [closeTime, setCloseTime] = useState('02:59');
  
  // Reset form values when dialog opens or belt changes
  React.useEffect(() => {
    if (open) {
      setOpenTime(currentSchedule?.openTime || '03:00');
      setCloseTime(currentSchedule?.closeTime || '02:59');
    }
  }, [open, beltNumber, currentSchedule]);

  const handleSave = () => {
    // Preserve fields this dialog doesn't edit (e.g. closed) so saving
    // times doesn't silently re-open a closed belt
    onSave(beltNumber, { ...(currentSchedule || {}), openTime, closeTime });
    onClose();
  };

  const handleClear = () => {
    onSave(beltNumber, null); // Remove schedule
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      PaperProps={{
        sx: {
          bgcolor: 'rgba(26, 26, 26, 0.95)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          minWidth: 350
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        color: '#C0C0C0',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccessTimeIcon />
          <Typography variant="h6">{beltName || `Belt ${beltNumber}`} Schedule</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
              Open Time
            </Typography>
            <TextField
              type="time"
              value={openTime}
              onChange={(e) => setOpenTime(e.target.value)}
              fullWidth
              sx={{
                '& .MuiInputBase-root': {
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  color: '#fff',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.08)',
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '& input[type="time"]::-webkit-calendar-picker-indicator': {
                  filter: 'invert(1) opacity(0.5)',
                  cursor: 'pointer'
                }
              }}
            />
          </Box>
          
          <Box>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
              Close Time
            </Typography>
            <TextField
              type="time"
              value={closeTime}
              onChange={(e) => setCloseTime(e.target.value)}
              fullWidth
              sx={{
                '& .MuiInputBase-root': {
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  color: '#fff',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.08)',
                  }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '& input[type="time"]::-webkit-calendar-picker-indicator': {
                  filter: 'invert(1) opacity(0.5)',
                  cursor: 'pointer'
                }
              }}
            />
          </Box>
          
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            Note: For overnight schedules, set close time earlier than open time (e.g., 22:00 - 06:00)
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ 
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        p: 2,
        gap: 1 
      }}>
        <Button 
          onClick={handleClear}
          sx={{ 
            color: 'rgba(255, 255, 255, 0.5)',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.05)'
            }
          }}
        >
          Clear Schedule
        </Button>
        <Button 
          onClick={onClose}
          sx={{ 
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.05)'
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          sx={{
            bgcolor: '#F9B612',
            color: '#000',
            '&:hover': {
              bgcolor: '#FFD700'
            }
          }}
        >
          Save Schedule
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BeltTimeDialog;